import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../lib/api';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Users, Languages, FileStack, CheckCircle, XCircle, Clock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import AppShell from '../components/AppShell';

export default function HRDashboard() {
  const navigate = useNavigate();
  const [interpreters, setInterpreters] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [langRequests, setLangRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [iRes, lRes, lrRes] = await Promise.all([adminAPI.getInterpreters(), adminAPI.getLanguages(), adminAPI.getLanguageRequests()]);
      setInterpreters(iRes.data);
      setLanguages(lRes.data);
      setLangRequests(lrRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleApproveInterpreter = async (id) => {
    try { await adminAPI.approveInterpreter(id); toast.success('Interpreter approved'); load(); } catch (e) { toast.error(e.response?.data?.detail || 'Cannot approve — check documents'); }
  };
  const handleRejectInterpreter = async (id) => {
    try { await adminAPI.rejectInterpreter(id); toast.success('Interpreter rejected'); load(); } catch { toast.error('Failed'); }
  };
  const handleApproveLangReq = async (id) => {
    try { await adminAPI.approveLanguageRequest(id); toast.success('Language request approved'); load(); } catch { toast.error('Failed'); }
  };
  const handleRejectLangReq = async (id) => {
    try { await adminAPI.rejectLanguageRequest(id); toast.success('Language request rejected'); load(); } catch { toast.error('Failed'); }
  };

  const statusIcon = (s) => {
    if (s === 'approved') return <CheckCircle className="w-4 h-4 text-green-600" />;
    if (s === 'rejected') return <XCircle className="w-4 h-4 text-red-600" />;
    return <Clock className="w-4 h-4 text-amber-500" />;
  };

  if (loading) return <AppShell><div className="flex items-center justify-center h-[calc(100vh-3.5rem)]"><Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--brand))]" /></div></AppShell>;

  const pendingInterpreters = interpreters.filter(i => i.approval_status === 'pending');
  const pendingRequests = langRequests.filter(r => r.status === 'pending');

  return (
    <AppShell>
      <div className="p-6 md:p-8 max-w-6xl animate-fade-in" data-testid="hr-dashboard">
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight mb-2" style={{ fontFamily: 'DM Sans' }}>Human Resources Dashboard</h1>
        <p className="text-muted-foreground text-sm mb-6">Interpreters, languages, and requests</p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          {[
            { label: 'Pending Interpreters', value: pendingInterpreters.length, icon: Users, color: 'text-amber-500' },
            { label: 'Languages', value: languages.length, icon: Languages, color: 'text-[hsl(var(--brand))]' },
            { label: 'Pending Requests', value: pendingRequests.length, icon: FileStack, color: 'text-blue-600' },
          ].map((s, i) => (
            <div key={i} className="rounded-lg border bg-card p-4">
              <div className="flex items-center justify-between"><p className="text-xs text-muted-foreground">{s.label}</p><s.icon className={`w-4 h-4 ${s.color}`} /></div>
              <p className="text-2xl font-bold mt-1" style={{ fontFamily: 'DM Sans' }}>{s.value}</p>
            </div>
          ))}
        </div>

        <Tabs defaultValue="interpreters" className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="interpreters" data-testid="hr-interpreters-tab">Interpreters ({interpreters.length})</TabsTrigger>
            <TabsTrigger value="languages" data-testid="hr-languages-tab">Languages ({languages.length})</TabsTrigger>
            <TabsTrigger value="requests" data-testid="hr-requests-tab">Requests ({langRequests.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="interpreters">
            <div className="rounded-lg border bg-card">
              <ScrollArea className="h-[calc(100vh-24rem)]">
                <Table>
                  <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Languages</TableHead><TableHead>Rating</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {interpreters.map(i => (
                      <TableRow key={i.user_id} data-testid={`interp-row-${i.user_id}`}>
                        <TableCell className="font-medium">{i.name}</TableCell>
                        <TableCell><div className="flex flex-wrap gap-1">{(i.languages || []).map(l => <Badge key={l} variant="outline" className="text-[10px]">{l}</Badge>)}</div></TableCell>
                        <TableCell className="font-mono">{i.rating || '-'}</TableCell>
                        <TableCell><div className="flex items-center gap-1">{statusIcon(i.approval_status)}<span className="text-sm">{i.approval_status}</span></div></TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" className="h-7" onClick={() => navigate('/admin/interpreters')}>View</Button>
                            {i.approval_status === 'pending' && (
                              <>
                                <Button size="sm" className="h-7 bg-green-600 text-white text-xs" onClick={() => handleApproveInterpreter(i.user_id)} data-testid={`approve-interp-${i.user_id}`}>Approve</Button>
                                <Button size="sm" variant="destructive" className="h-7 text-xs" onClick={() => handleRejectInterpreter(i.user_id)} data-testid={`reject-interp-${i.user_id}`}>Reject</Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </div>
          </TabsContent>

          <TabsContent value="languages">
            <div className="rounded-lg border bg-card p-1">
              <Table>
                <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
                <TableBody>
                  {languages.map(l => (
                    <TableRow key={l.id || l.code}><TableCell className="font-mono">{l.code}</TableCell><TableCell>{l.name}</TableCell>
                      <TableCell><Button variant="ghost" size="sm" className="h-7" onClick={() => navigate('/admin/languages')}>Edit</Button></TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="requests">
            <div className="rounded-lg border bg-card">
              <ScrollArea className="h-[calc(100vh-24rem)]">
                <Table>
                  <TableHeader><TableRow><TableHead>Interpreter</TableHead><TableHead>Language</TableHead><TableHead>Status</TableHead><TableHead>Date</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {langRequests.map(r => (
                      <TableRow key={r.id}>
                        <TableCell className="text-sm">{r.interpreter_name}</TableCell>
                        <TableCell><Badge variant="outline">{r.language_code}</Badge></TableCell>
                        <TableCell><div className="flex items-center gap-1">{statusIcon(r.status)}<span className="text-sm">{r.status}</span></div></TableCell>
                        <TableCell className="text-xs text-muted-foreground">{r.created_at ? new Date(r.created_at).toLocaleDateString() : '-'}</TableCell>
                        <TableCell>
                          {r.status === 'pending' && (
                            <div className="flex gap-1">
                              <Button size="sm" className="h-7 bg-green-600 text-white text-xs" onClick={() => handleApproveLangReq(r.id)} data-testid={`approve-lr-${r.id}`}>Approve</Button>
                              <Button size="sm" variant="destructive" className="h-7 text-xs" onClick={() => handleRejectLangReq(r.id)} data-testid={`reject-lr-${r.id}`}>Reject</Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {langRequests.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No language requests</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </ScrollArea>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </AppShell>
  );
}
